/**
 * Swal2 wrapper for eases alert configuration.
 *
 * @see https://sweetalert2.github.io/
 *
 * @summary Dialogs utility.
 * @author Alvear Candia, Cristopher Alejandro <caalvearc@achs.cl>
 *
 * Created at     : 2020-05-16 16:38:41
 * Last modified  : 2021-03-23 15:19:25
 */

import SwalDefault from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import '../css/swal.scss';

const SwalReact = withReactContent(SwalDefault);

export default {
    /**
     * Shows a sweet alert dialog.
     *
     * @see https://sweetalert2.github.io/#configuration
     *
     * @param {SweetAlertOptions} options Swal 2 configuration.
     *
     * @returns {Promise<SweetAlertResult<any>>} Popup.
     */
    show: (options) => SwalReact.fire(options),

    /**
     * Shows a confirm dialog.
     *
     * @param {string} type dialog type from SweetAlert2.
     * @param {string | React.ReactElement} content JSX content for the dialog.
     * @param {string} [confirmText] text for confirm button.
     *
     * @returns {Promise<SweetAlertResult<any>>} Popup.
     */
    confirm: (type, content, confirmText = 'Aceptar') => SwalReact.fire({
        icon: type,
        html: content,
        confirmButtonText: confirmText,
        allowOutsideClick: false
    }),

    /**
     * Shows a alert popup.
     *
     * @param {string} type dialog type from SweetAlert2.
     * @param {string | React.ReactElement} content JSX content for the dialog.
     * @param {string} [confirmText] text for confirm button.
     *
     * @returns {Promise<SweetAlertResult<any>>} Popup.
     */
    alert: (type, content, confirmText = 'Aceptar') => SwalReact.fire({
        icon: type,
        html: content,
        confirmButtonText: confirmText,
        showCancelButton: false,
        showConfirmButton: false
    }),

    /**
     * Shows a alert popup.
     *
     * @param {string} type dialog type from SweetAlert2.
     * @param {string | React.ReactElement} content JSX content for the dialog.
     * @param {string} [confirmText] text for confirm button.
     *
     * @returns {Promise<SweetAlertResult<any>>} Popup.
     */
     alertConfirm: (type, content, confirmText = 'Aceptar') => SwalReact.fire({
        icon: type,
        html: content,
        confirmButtonText: confirmText,
        showCancelButton: false,
        showConfirmButton: true
    }),

    /**
     * Shows a confirm dialog with Ok and Cancel button.
     *
     * @param {string} type dialog type from SweetAlert2.
     * @param {string | React.ReactElement} content JSX content for the dialog.
     * @param {string} [confirmText] text for confirm button.
     * @param {string} [cancelText] text for cancel button.
     *
     * @returns {Promise<SweetAlertResult<any>>} Popup.
     */
    dialog: (type, content, confirmText = 'Sí', cancelText = 'No') => SwalReact.fire({
        icon: type,
        html: content,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        showCancelButton: true,
        allowOutsideClick: false,
        reverseButtons: true
    }),

    /**
     * Shows a confirm dialog with Ok and Cancel button.
     *
     * @param {string} type dialog type from SweetAlert2.
     * @param {string | React.ReactElement} content JSX content for the dialog.
     * @param {string} [confirmText] text for confirm button.
     * @param {string} [cancelText] text for cancel button.
     * @param {boolean} [reverseButtons] boolean for show in orden buttons
     * @param {boolean} [showCloseButton] boolean for show close button
     * @param {string} [cancelButtonClass] text for class of cancel button
     * @param {string} [confirmButtonClass] text for class of confirm button
     * @param {string} [popupClassp] text for class of popup
     *
     * @returns {Promise<SweetAlertResult<any>>} Popup.
     */
    dialogConfirm: (type, content, confirmText = 'Sí', denyText = 'No', reverseButtons = false, showCloseButton = false, denyButtonClass = '', confirmButtonClass = '', popupClass = '') => SwalReact.fire({
        icon: type,
        html: content,
        confirmButtonText: confirmText,
        denyButtonText: denyText,
        showCancelButton: false,
        showDenyButton: true,
        allowOutsideClick: false,
        reverseButtons,
        showCloseButton,
        customClass: {
            denyButton: denyButtonClass,
            confirmButton: confirmButtonClass,
            popup: popupClass
        }
    }),

    alertCloseButton: (type, content, confirmText = 'Aceptar') => SwalReact.fire({
        icon: type,
        html: content,
        confirmButtonText: confirmText,
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
    }),
};
